import React, {useState, useEffect, useRef, lazy, Suspense} from "react";

import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { Grid } from "@mui/material";
import { BalloonNotification } from "../../../components/BalloonNotification";
import { CompReportOwnMunicipalityTable } from "./reportOwnMunicipalityTableComponent";
import { DonutChart } from "./DonutChart";
import { getOrganizations } from "../../../components/organizationUtils";
import {useTranslation} from "react-i18next";
import {listLocations} from "../../location/location.actions";
import { getServiceOptions } from "../../function/function.actions";
import { hasPermission } from "../../../permissions";
import { PERMISSION_VIEW_ANY_MUNICIPALITY } from "../../../permissions";
import {
  getOrganizationTree
} from "../../municipality/municipality.actions";
import {
  getOwnMunicipalityReport,
  getOwnMunicipalityReportSuccess,
} from "../report.actions";
import moment from "moment";
import {getComparator} from "../../../utils";
import { ButtonPrimary } from "../../../components/newComponents/ButtonPrimary";
import {CompDropdownNew} from "../../../components/newComponents/DropdownNew";
import {CompCheckbox} from "../../../components/Checkbox";
import { CompTextFieldNew } from "../../../components/newComponents/TextField";
import { CompDatePickerNew } from "../../../components/newComponents/DatePickerNew";
import { SearchMultiselectDropdown } from "../../../components/newComponents/SearchMultiselectDropdown";

const BarChart = lazy(() => import('./BarChart'));

const mapStateToProps = (state) => {
  return {
    municipalities: state.municipality.municipalities,
    authenticatedUser: state.auth.authenticatedUser,
    organizationTree: state.municipality.organizationTree,
    locations: state.location.locations,
    services: state.root.services,
    contentIdentifiers: state.root.contentIdentifiers,
    methods: state.root.methods,
  };
};

const formatLocations = (locationList, locationArray) => {
  var formatted = [];
  for (let i = 0; i < locationArray.length; i++) {
    if (locationArray[i] > -1) {
      formatted.push(""+locationArray[i]);
      formatted.push(locationList.filter(l => l.id === locationArray[i])[0].name);
    }
    else {
      var name = locationList.filter(l => l.id === locationArray[i])[0].name;
      formatted.push(name.slice(0,name.lastIndexOf(" (")));
    }
  }
  return formatted;
}

const mapDispatchToProps = (dispatch) => {
  return {
    getOwnMunicipalityReport: (municipalityId, startDate, endDate, functionName, locationList, locationArray, organizationArray, serviceArray, ownFunctions, methodArray, groupingType, language, onSuccess) =>
      dispatch(getOwnMunicipalityReport(municipalityId, startDate, endDate, functionName, formatLocations(locationList, locationArray), organizationArray, serviceArray, ownFunctions, methodArray, groupingType, language, onSuccess)),
    getOrganizationTree: (municipalityId, level) => dispatch(getOrganizationTree(municipalityId, level)),
    listLocations: (municipalityId) => dispatch(listLocations(municipalityId, true))
  };
};

const ReportPage = ({
    municipalities,
    listLocations,
    getOrganizationTree,
    getOwnMunicipalityReport,
    authenticatedUser,
    organizationTree,
    locations,
    services,
    methods,
    contentIdentifiers
  }) => {
  const {t} = useTranslation('translation');

  const [municipality, setMunicipality] = useState(null);
  const [municipalityList, setMunicipalityList] = useState([]);
  const [startDate, setStartDate] = useState(moment.utc().subtract(14, "days").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment.utc().format("YYYY-MM-DD"));
  const [functionName, setFunctionName] = useState("");
  const [locationArray, setLocationArray] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [organizationArray, setOrganizationArray] = useState([]);
  const [organizationList, setOrganizationList] = useState([]);
  const [serviceArray, setServiceArray] = useState([]);
  const [methodArray, setMethodArray] = useState([]);
  const [data, setData] = useState({});
  const [timer, setTimer] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(t('report.current_language'));
  const [isMobile, setIsMobile] = useState(false);
  const [showNationalContentIdentifiers, setShowNationalContentIdentifiers] = useState(false);
  const [showRegionalContentIdentifiers, setShowRegionalContentIdentifiers] = useState(false);
  const targetRef = useRef();

  const [ownFunctions, setOwnFunctions] = useState({checked: false})
  const groupingTypeOptions = [
    { name: t("report.function_content_identifiers"), code: "CONTENT_IDENTIFIERS"},
    { name: t("report.function_methods"), code: "METHODS"},
    /* { name: t("report.function_organizations"), code: "ORGANIZATIONS"}, */
    { name: t("report.function_locations"), code: "LOCATIONS"},
    { name: t("report.function_services"), code: "SERVICES"},
    { name: t("report.function_event_types"), code: "EVENT_TYPES"},
  ]

  const [groupingType, setGroupingType] = useState(groupingTypeOptions[0])
  const [groupingTypeValue, setGroupingTypeValue] = useState(groupingTypeOptions[0].code)

  if (currentLanguage !== t('report.current_language')) {
    setCurrentLanguage(t('report.current_language'));
  }

  useEffect(() => {
    const deviceCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(deviceCheck)
  }, [])

  const canChangeMunicipality = hasPermission(PERMISSION_VIEW_ANY_MUNICIPALITY, authenticatedUser);

  const getMethods = () => {
    const response = [];
    for (const i in methods) {
      const method = methods[i];
      if (method.displayOrder !== -1) {
        response.push({value: method.id, label: t('report.current_language') === "FINNISH" ? method.nameFi : method.nameSv, descriptionFi: method.descriptionFi, descriptionSv: method.descriptionSv})
      }
    }
    response.sort((a,b) => a.label.localeCompare(b.label));
    return response;
  }

  const [isGenerating, setIsGenerating] = useState(false);

  const handleToPDF = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    const { default: generatePDF, Margin } = await import("react-to-pdf");

    const options = {
      method: "save",
      filename: "minun_kuntani.pdf",
      page: { margin: Margin.MEDIUM },
      canvas: {
        useCORS: true,
        scale: 2
      }
    };

    await generatePDF(targetRef, options);

    setIsGenerating(false);
  }

  const handleCaptureAll = async () => {
    if (isGenerating) return;
    setIsGenerating(true);

    const { jsPDF } = await import('jspdf');
    const { default: html2canvas } = await import("html2canvas");
    const screenshots = document.querySelectorAll('.data');
  
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
    const orientation = isMobile ? 'p' : 'l'; 
    const pageSize = isMobile ? 'a4' : [800, 600]; 
  
    const pdf = new jsPDF({
      orientation: orientation,
      unit: 'px',
      format: pageSize
    });
  
    let index = 0;
  
    const addScreenshotToPdf = () => {
      if (index < screenshots.length) {
        const screenshot = screenshots[index];
        html2canvas(screenshot, { 
          scale: 2,
          useCORS: true, 
          logging: false 
        })
          .then((canvas) => {
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const widthRatio = pageWidth / canvas.width;
            const heightRatio = pageHeight / canvas.height;
            const ratio = widthRatio > heightRatio ? heightRatio : widthRatio;
  
            const canvasWidth = canvas.width * ratio;
            const canvasHeight = canvas.height * ratio;
  
            const imgData = canvas.toDataURL('image/png');
  
            if (index === 0) {
              pdf.addImage(new Uint8Array(imgData, 'PNG', 0, 0, canvasWidth, canvasHeight));
            } else if(index === 1) {
                if(isMobile === true) {
                  pdf.addPage()
                  pdf.addImage(new Uint8Array(imgData, 'PNG', 0, 0, canvasWidth, canvasHeight));
                } else {
                  pdf.addImage(new Uint8Array(imgData, 'PNG', 0, 100, canvasWidth, canvasHeight));
                }
            } else {
              pdf.addPage();
              pdf.addImage(new Uint8Array(imgData, 'PNG', 0, 0, canvasWidth, canvasHeight));
            }
  
            index++;
            addScreenshotToPdf();
          });
      } else {
        pdf.save('screenshots.pdf');
      }
    };
    addScreenshotToPdf();
    setIsGenerating(false);
  };

  const handleMunicipalityChange = (event) => {
    municipalities.forEach((municipality) => {
      if (municipality.id === parseInt(event.target.value)) {
        setMunicipality(municipality);
      }
    })
  };

  useEffect(() => {
    if (municipality) {
      getOrganizationTree(municipality.id, 10);
      listLocations(municipality.id);
    }
  }, [municipality])

  useEffect(() => {
    const comparator = getComparator();
    const response = [];
    for (var municipality of municipalities) {
      response.push(municipality);
    }
    response.sort((a, b) => comparator.compare(a.name,b.name));
    setMunicipalityList(response);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipalities])

  useEffect(() => {
    getOrganizationTree(authenticatedUser.mainMunicipalityId, 10);
    listLocations(authenticatedUser.mainMunicipalityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    locations.sort((a, b) => {
      const aIsLast = a.id < 0;
      const bIsLast = b.id < 0;

      if (aIsLast && !bIsLast) return 1;
      if (!aIsLast && bIsLast) return -1;

      return a.name.localeCompare(b.name);
    });
    for (let i = 0; i < locations.length; i++) {
      if (locations[i].id < 0) {
        locations[i].name = locations[i].original + " (" + t("report.not_saved") +")";
      }
    }
    setLocationList(locations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations])

  useEffect( () => {
    for (let i = 0; i < locations.length; i++) {
      if (locations[i].id < 0) {
        locations[i].name = locations[i].original + " (" + t("report.not_saved") +")";
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage])

  useEffect(() => {
    setOrganizationList(getOrganizations(organizationTree));
  }, [organizationTree])

  useEffect(() => {
    getReportTimer();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipality, startDate, endDate, functionName, locationArray, organizationArray, serviceArray, methodArray, groupingType, ownFunctions])


  const getReportTimer = () => {
    if (timer) {
      clearTimeout(timer)
    }

    const newTimer = setTimeout(() => {
      getReport();
    }, 1000);
    setTimer(newTimer);

    return () => {
      clearTimeout(newTimer);
      setTimer(null);
    };
  }

  const getReport = () => {
    if (startDate !== null && endDate !== null) {
      const locations = [];
      for (var i = 0; i < locationArray.length; i++) {
        locations.push(locationArray[i].id);
      }
      const organizations = [];
      for (i = 0; i < organizationArray.length; i++) {
        organizations.push(organizationArray[i].id);
      }
      const services = [];
      for (i = 0; i < serviceArray.length; i++) {
        services.push(serviceArray[i].value);
      }
      const methods = [];
      for (i = 0; i < methodArray.length; i++) {
        methods.push(methodArray[i].value);
      }


      getOwnMunicipalityReport(
        canChangeMunicipality ? (municipality ? municipality.id : null) : null,
        startDate,
        endDate,
        functionName,
        locationList,
        locations,
        organizations,
        services,
        ownFunctions.checked,
        methods,
        groupingType.code,
        currentLanguage,
        (data) => {
          setData(data);
          return getOwnMunicipalityReportSuccess(data);
        }
      );
    }
  };


  const getKey = (key, element, defaultValue) => {
    try {
      if (!element[key]) {
        return defaultValue;
      }
      return element[key];
    } catch (err) {
      return defaultValue;
    }
  };

  const getTotal = (key) => getKey(key, data.total, 0);

  const getCollection = (key, translationPath) => {
    const currentData = getKey(key, data.collection, []);

    if (translationPath !== undefined && translationPath !== null) {
      currentData.forEach((item) => {
        const translatedTitle = t(
          `${translationPath}.${item.title}`
        );
        item.title = translatedTitle.substring(
          translatedTitle.lastIndexOf(".") + 1
        );
      });
    }
    return currentData;
  };

  const handleLocationChange = (row, isAddAction, index, i) => {
    let value = [...locationArray];
    if (isAddAction) {
      value.push(row);
    } else {
      value.splice(i, 1);
    }
    setLocationArray(value);
  }

  const handleOrganizationChange = (row, isAddAction, index, i) => {
    let value = [...organizationArray];
    if (isAddAction) {
      value.push(row);
    } else {
      value.splice(i, 1);
    }
    setOrganizationArray(value);
  }

  const handleServiceChange = (row, isAddAction, index, i) => {
    let value = [...serviceArray];
    if (isAddAction) {
      value.push(row);
    } else {
      value.splice(i, 1);
    }
    setServiceArray(value);
  }

  const handleMethodChange = (row, isAddAction, index, i) => {
    let value = [...methodArray];
    if (isAddAction) {
      value.push(row);
    } else {
      value.splice(i, 1);
    }
    setMethodArray(value);
  }


  const handleGroupingTypeChange = (e) => {
    const selectedCode = e.target.value;
    const selectedOption = groupingTypeOptions.find(option => option.code === selectedCode);

    if (selectedOption) {
      setGroupingType({ name: selectedOption.name, code: selectedOption.code });
    }
    setGroupingTypeValue(selectedCode);
  }

  const getWeekSummaries = () => getKey("weekSummaries", data, []);

  const selectedOrganizations = [];
  if (organizationArray) {
      organizationArray.forEach(o => {
        selectedOrganizations.push(o.id);
      });
  }
  const selectedLocations = [];
  if (locationArray) {
      locationArray.forEach(l => {
        selectedLocations.push(l.id);
      });
  }
  const selectedServices = [];
  if (serviceArray) {
    serviceArray.forEach(s => {
      selectedServices.push(s.value);
    });
  }
  const selectedMethods = [];
  if (methodArray) {
    methodArray.forEach(s => {
      selectedMethods.push(s.value);
    });
  }

  const handleCheckboxChange = (e) => {
    setOwnFunctions({checked: !ownFunctions.checked})
  }

  const setRegionalContentIdentifiers = (value) => {
    if(showRegionalContentIdentifiers) {
      setShowRegionalContentIdentifiers(false);
    } else {
      setShowRegionalContentIdentifiers(value);
      setShowNationalContentIdentifiers(false);
    }
  }

  const setNationalContentIdentifiers = (value) => {
    if(showNationalContentIdentifiers) {
      setShowNationalContentIdentifiers(false);
    } else {
      setShowNationalContentIdentifiers(value);
      setShowRegionalContentIdentifiers(false);
    }
  }

  const boxHeight = getCollection("sumOfEventGroupings", getKey("groupingType", data.collection, null) === "EVENT_TYPES" ? "function" : null).length * 40 + 100
  console.log(groupingTypeValue)
  return (
    <div className={"reports"} ref={targetRef}>
      <Grid container spacing={1} >
        {canChangeMunicipality && <Grid size={{xs: 12,  md: 6}}>
            <CompDropdownNew
              label={t('common.municipality')}
              isLabelShrink={true}
              valueColumn={"id"}
              labelColumn={"name"}
              disabled={!canChangeMunicipality}
              rows={municipalityList}
              hasEmptyOption={false}
              onChange={handleMunicipalityChange}
              name="municipalityId"
              selectedValue={municipality ? municipality.id : null}
            />
        </Grid>}
        <Grid size={{xs: 12, md: canChangeMunicipality ? 6 :  4}}>
          <CompTextFieldNew
            label={`${t('function.function_name')}`}
            value={functionName}
            name="functionName"
            isLabelShrink={true}
            onChange={e => setFunctionName(e.target.value)}
          />
        </Grid>
          <Grid size={{xs: 12, sm: 4}}>
            <CompDatePickerNew
              label={t('report.select_interval')}
              value={startDate}
              name="startDate"
              onChange={e => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 4}}>
            <CompDatePickerNew
              label={t('function.end_date')}
              value={endDate}
              name="endDate"
              onChange={e => setEndDate(e.target.value)}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 4}}>
            <SearchMultiselectDropdown
              label={`${t('function.service')}`}
              selectedRows={selectedServices}
              rows={getServiceOptions(t, services)}
              keyColumn={'value'}
              valueFunc={(row) => row.label}
              name="services"
              parentsUnselectable={true}
              onSelect={handleServiceChange}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 4}}>
            <SearchMultiselectDropdown
                label={`${t('common.methods')}`}
                selectedRows={selectedMethods}
                rows={getMethods()}
                keyColumn={'value'}
                valueFunc={(row) => row.label}
                name="methods"
                parentsUnselectable={true}
                onSelect={handleMethodChange}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 4}}>
            <SearchMultiselectDropdown
              label={`${t('common.locations')}`}
              selectedRows={selectedLocations}
              rows={locationList}
              keyColumn={'id'}
              valueFunc={(row) => row.name}
              name="locations"
              onSelect={handleLocationChange}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 4}}>
            <SearchMultiselectDropdown
              label={`${t('user.organizations')}`}
              selectedRows={selectedOrganizations}
              rows={organizationList}
              keyColumn={'id'}
              valueFunc={(row) => row.name}
              name="organizations"
              onSelect={handleOrganizationChange}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 4}}>
            <CompCheckbox
                rows={[
                  {
                    label: t('function.your_functions'),
                    key: "checked",
                    value: ownFunctions.checked
                  }
                ]}
                booleanmode={true}
                selected={ownFunctions}
                onChange={handleCheckboxChange}
                name={"checked"}
            />
          </Grid>
    </Grid>
        <Grid id="pdfDoc1" className="data" size={12}>
          <div className={"balloon-row"}>
            <BalloonNotification
              title={getTotal("eventCount")}
              content={t("report.functions")}
              severity={"notification"}
            />
            <BalloonNotification
              title={getTotal("sumOfEventHours")}
              content={t("report.event_hours")}
              severity={"notification"}
            />
            <BalloonNotification
              title={getTotal("participantCount")}
              content={t("report.participants")}
              severity={"notification"}
            />
            <BalloonNotification
              title={getTotal("youthOrganizersCount")}
              content={t("report.youth_organizers")}
              severity={"notification"}
            />
            <BalloonNotification
              title={getTotal("sumOfYouthOrganizersSEventHours")}
              content={t("report.youth_organizers_s_event_hours")}
              severity={"notification"}
            />
          </div>
        </Grid>
        <Grid id="pdfDoc2" className="data" sx={{width: '100%'}}>
          <div className={"column-box-container"}>
            <Grid container spacing={2} className={"column-box-title"}>
              <Grid size={{xs: 6, sm: 4}}>
                <div className="label">{groupingType.name}</div>
              </Grid>
              <Grid className={"chart-header-box"} size={{xs: 12, sm: 8}} style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px'}} sx={{'& .MuiSvgIcon-root': { color: '#ffffff' },'& .Mui-checked .MuiSvgIcon-root': { color: '#ffffff' }}}>
                {groupingTypeValue === "CONTENT_IDENTIFIERS" && (
                  <div className={"checkbox-wrapper"} style={{ display: 'flex', alignItems: 'center', color: '#ffffff', whiteSpace: 'nowrap' }}>
                    <CompCheckbox
                      rows={[
                        {
                          label: <span style={{ color: '#ffffff' }}>{t('report.regional_content_identifiers', 'Kunnalliset')}</span>,
                          key: "checked",
                          value: showRegionalContentIdentifiers
                        }
                      ]}
                      booleanmode={true}
                      selected={{checked: showRegionalContentIdentifiers}}
                      onChange={setRegionalContentIdentifiers}
                      name={"checked"}
                    />
                  </div>
                )}
                {groupingTypeValue === "CONTENT_IDENTIFIERS" && (
                  <div className={"checkbox-wrapper"} style={{ display: 'flex', alignItems: 'center', color: '#ffffff', whiteSpace: 'nowrap' }}>
                    <CompCheckbox
                      rows={[
                        {
                          label: <span style={{ color: '#ffffff' }}>{t('report.national_content_identifiers', 'Valtakunnalliset')}</span>,
                          key: "checked",
                          value: showNationalContentIdentifiers
                        }
                      ]}
                      booleanmode={true}
                      selected={{checked: showNationalContentIdentifiers}}
                      onChange={setNationalContentIdentifiers}
                      name={"checked"}
                    />
                  </div>
                  )}
                <Grid className="dropdown-selection" sx={{'& .MuiSvgIcon-root': { color: 'rgb(0 0 0 / 54%)' }}}>
                  <CompDropdownNew
                    className={'function-dropdown'}
                    isLabelShrink={true}
                    valueColumn={"code"}
                    labelColumn={"name"}
                    rows={groupingTypeOptions}
                    onChange={handleGroupingTypeChange}
                    name="groupingType"
                    selectedValue={groupingTypeValue}
                  />
                </Grid>
              </Grid>
            </Grid>
            <div className={"column-box-content"}>
              <Suspense fallback={<div>LOADING</div>}>
                <BarChart
                    data={getCollection("sumOfEventGroupings", getKey("groupingType", data.collection, null) === "EVENT_TYPES" ? "function" : null)}
                    valueField="value"
                    argumentField="title"
                    t={t}
                    groupingType={groupingTypeValue}
                    contentIdentifiers={contentIdentifiers}
                    showCombinedTotals={showNationalContentIdentifiers}
                    showChildrenOnly={showRegionalContentIdentifiers}
                />
              </Suspense>
            </div>
          </div>
        </Grid>
        <Grid
          id="pdfDoc3"
          className="data"
          size={{
            xs: 12,
            md: 6
          }}>
          <div className={"column-box-container"}>
            <div className={"column-box-title"}>
              {t("report.participants")}
            </div>
            <div className={"column-box-content"}>
              <DonutChart
                data={getKey(
                  "sumOfParticipantGenders",
                  data.collection,
                  []
                )}
                valueField="value"
                argumentField="title"
                t={t}
                translationPath="function"
              />
            </div>
          </div>
        </Grid>
        <Grid
          id="pdfDoc4"
          className="data"
          size={{
            xs: 12,
            md: 6
          }}>
          <div className={"column-box-container"}>
            <div className={"column-box-title"}>
              {t("report.participant_genders")}
            </div>
            <div className={"column-box-content"}>
              <DonutChart
                data={getKey(
                  "sumOfParticipantAges",
                  data.collection,
                  []
                )}
                valueField="value"
                argumentField="title"
                t={t}
                translationPath="function"
              />
            </div>
          </div>
        </Grid>
      <Grid id="pdfDoc5" className="data" size={12}>
        <CompReportOwnMunicipalityTable
          rows={getWeekSummaries()}
          label={t("report.week_report")}
        />
      </Grid>
      <ButtonPrimary onClick={() => {
        if(isMobile) {
          console.log(isMobile)
          handleCaptureAll()
        } else {
          console.log(isMobile)
          handleToPDF()
        }
      }}>{t('report.load_pdf')}</ButtonPrimary>
    </div>
  );
}

const OwnMunicipalityReportPage = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation("translation")(ReportPage));

export { OwnMunicipalityReportPage };
